import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@/lib/api-hooks';
import { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { Settings2 } from 'lucide-react';
import { authClient } from '@/lib/auth-client';
import { backendApi } from '@/lib/backend';
import { useStore } from '@/store/useStore';
import { setApiCacheScope } from '@/services/api';
import { AppShell, PageTitle, type ShellNavItem } from '@/components/layout/AppShell';
import { ClientSwitcher } from '@/components/dashboard/ClientSwitcher';
import { TodayStats } from '@/components/dashboard/TodayStats';
import { RecentCalls } from '@/components/dashboard/RecentCalls';
import { LiveDashboardWidgets } from '@/components/dashboard/LiveDashboardWidgets';
import { QuickActionsBar } from '@/components/dashboard/QuickActionsBar';
import { ExtensionStatusDashboard } from '@/components/dashboard/ExtensionStatus';
import { QueueMonitor } from '@/components/dashboard/QueueMonitor';
import { QueueAgentBoard } from '@/components/dashboard/QueueAgentBoard';
import {
  DashboardSectionLinks,
  type DashboardSection,
} from '@/components/dashboard/DashboardSectionNav';
import { ActiveCallControls } from '@/components/dashboard/ActiveCallControls';
import { InboundRoutingManager } from '@/components/dashboard/InboundRoutingManager';
import { SimplifiedAnalytics } from '@/components/dashboard/SimplifiedAnalytics';
import { CrmLayout } from '@/components/crm/CrmLayout';
import { CrmProvider } from '@/components/crm/CrmContext';
import { CrmOverlays } from '@/components/crm/CrmOverlays';
import { DesktopPopupApp } from '@/components/crm/DesktopPopup';
import { isDesktopPopupWindow } from '@/lib/desktopApp';
import { ReportingLayout } from '@/components/reporting/ReportingLayout';
import { ProvisioningLayout } from '@/components/provisioning/ProvisioningLayout';
import { ChatLayout } from '@/components/chat/ChatLayout';
import { AppLoginForm } from '@/components/auth/AppLoginForm';
import { AdminConsole } from '@/components/admin/AdminConsole';
import { Card } from '@/components/ui/Card';
import {
  MODULE_DEFINITIONS,
  canManageModule,
  getFirstEnabledModule,
  isNavModuleEnabled,
  normalizeModuleAccess,
  type ModuleAccess,
  type ModuleDefinition,
} from '@/modules';
import type { AppView } from '@/types';

interface SessionContext {
  user: {
    id: string;
    email: string;
    name: string;
    role: 'admin' | 'technician' | 'customer';
  };
  customer: {
    id: string;
    name: string;
    status: 'active' | 'disabled';
  } | null;
  moduleAccess: ModuleAccess;
}

type DashboardSectionKey =
  | 'quickActions'
  | 'liveWidgets'
  | 'todayStats'
  | 'analytics'
  | 'queueMonitor'
  | 'queueAgents'
  | 'activeCalls'
  | 'inboundRouting'
  | 'extensionStatus'
  | 'recentCalls';

const sectionId = (key: DashboardSectionKey) => `dashboard-${key}`;

// Order matches the dashboard layout so the section links read top to bottom.
// Quick actions sit in the page title, so they have no link.
const DASHBOARD_SECTIONS: Array<DashboardSection & { key: DashboardSectionKey }> = (
  [
    ['liveWidgets', 'Live overview'],
    ['todayStats', 'Today stats'],
    ['analytics', 'Analytics'],
    ['queueMonitor', 'Queue monitor'],
    ['queueAgents', 'Queue agents'],
    ['activeCalls', 'Active calls'],
    ['inboundRouting', 'Inbound routing'],
    ['extensionStatus', 'Extension status'],
    ['recentCalls', 'Recent calls'],
  ] as const
).map(([key, label]) => ({ key, id: sectionId(key), label }));

function moduleNavItems(
  modules: ModuleDefinition[],
  activeId: AppView | null,
  onSelect: (id: AppView) => void
): ShellNavItem[] {
  return modules.map((module) => ({
    id: module.id,
    label: module.label,
    icon: module.icon,
    active: module.id === activeId,
    onSelect: () => onSelect(module.id),
  }));
}

function LoadingScreen({ text }: { text: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Card>
        <p className="text-gray-700 dark:text-gray-300">{text}</p>
      </Card>
    </div>
  );
}

function UnprovisionedAccount() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="max-w-md">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          Account is not provisioned
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Your login exists, but it has not been assigned to an admin or customer profile.
        </p>
        <button
          type="button"
          onClick={() => void authClient.signOut()}
          className="mt-4 px-4 py-2 rounded-lg bg-primary-600 text-white"
        >
          Sign out
        </button>
      </Card>
    </div>
  );
}

function CustomerExperience({
  context,
  topLeading,
  general,
}: {
  context: SessionContext;
  topLeading?: ReactNode;
  general?: ShellNavItem[];
}) {
  const { theme, currentView, setCurrentView } = useStore();
  // Runs during render (idempotent) so child widgets never read the previous client's cache.
  if (context.customer?.id) setApiCacheScope(context.customer.id);
  const moduleAccess = normalizeModuleAccess(context.moduleAccess);
  const isStaff =
    context.user.role === 'admin' || context.user.role === 'technician';
  const enabledModules = MODULE_DEFINITIONS.filter(
    (module) => isStaff || isNavModuleEnabled(moduleAccess, module)
  );
  const canManageDashboard = isStaff || canManageModule(moduleAccess, 'dashboard');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  useEffect(() => {
    const currentModule = MODULE_DEFINITIONS.find((m) => m.id === currentView);
    if (currentModule && !isNavModuleEnabled(moduleAccess, currentModule)) {
      const fallbackView = getFirstEnabledModule(moduleAccess);
      if (fallbackView) {
        setCurrentView(fallbackView);
      }
    }
  }, [currentView, moduleAccess, setCurrentView]);

  const handleRefreshExtensions = () => Promise.resolve();
  const handleRefreshQueues = () => Promise.resolve();
  const handleRefreshCalls = () => Promise.resolve();
  const handleRefreshAll = () => Promise.resolve();

  const widgets = moduleAccess.dashboardWidgets;
  const show = {
    quickActions: widgets.quickActions && canManageDashboard,
    liveWidgets: widgets.liveWidgets,
    todayStats: widgets.todayStats,
    analytics: widgets.analytics,
    queueMonitor: widgets.queueMonitor,
    queueAgents: widgets.queueMonitor,
    activeCalls: widgets.activeCalls,
    inboundRouting: widgets.inboundRouting,
    extensionStatus: widgets.extensionStatus,
    recentCalls: widgets.recentCalls,
  };
  const dashboardSections = DASHBOARD_SECTIONS.filter((section) => show[section.key]);

  const renderDashboard = () => {
    const hasAnyWidget = Object.values(widgets).some(Boolean);

    if (!hasAnyWidget) {
      return (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Dashboard is enabled, but all widgets are disabled
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Ask your administrator to enable at least one dashboard widget.
          </p>
        </Card>
      );
    }

    return (
      <>
        <PageTitle
          title="Dashboard"
          subtitle={
            context.customer?.name
              ? `Calls, queues and extensions for ${context.customer.name}.`
              : 'Calls, queues and extensions at a glance.'
          }
          actions={
            show.quickActions && (
              <QuickActionsBar
                onRefreshExtensions={handleRefreshExtensions}
                onRefreshQueues={handleRefreshQueues}
                onRefreshCalls={handleRefreshCalls}
                onRefreshAll={handleRefreshAll}
              />
            )
          }
        />
        <motion.div
          key="dashboard"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {show.liveWidgets && (
            <section id={sectionId('liveWidgets')} className="scroll-mt-6">
              <LiveDashboardWidgets />
            </section>
          )}
          {show.todayStats && (
            <section id={sectionId('todayStats')} className="scroll-mt-6">
              <TodayStats />
            </section>
          )}
          {show.analytics && (
            <section id={sectionId('analytics')} className="scroll-mt-6">
              <SimplifiedAnalytics />
            </section>
          )}

          {show.queueMonitor && (
            <section id={sectionId('queueMonitor')} className="scroll-mt-6">
              <QueueMonitor />
            </section>
          )}
          {show.queueAgents && (
            <section id={sectionId('queueAgents')} className="scroll-mt-6">
              <QueueAgentBoard />
            </section>
          )}

          {show.activeCalls && (
            <section id={sectionId('activeCalls')} className="scroll-mt-6">
              <ActiveCallControls readOnly={!canManageDashboard} />
            </section>
          )}
          {show.inboundRouting && (
            <section id={sectionId('inboundRouting')} className="scroll-mt-6">
              <InboundRoutingManager readOnly={!canManageDashboard} />
            </section>
          )}
          {(show.extensionStatus || show.recentCalls) && (
            <div
              className={
                show.extensionStatus && show.recentCalls
                  ? 'grid grid-cols-1 lg:grid-cols-2 gap-6'
                  : undefined
              }
            >
              {show.extensionStatus && (
                <section id={sectionId('extensionStatus')} className="scroll-mt-6">
                  <ExtensionStatusDashboard />
                </section>
              )}
              {show.recentCalls && (
                <section id={sectionId('recentCalls')} className="scroll-mt-6">
                  <RecentCalls />
                </section>
              )}
            </div>
          )}
        </motion.div>
      </>
    );
  };

  const renderCurrentView = () => {
    if (enabledModules.length === 0) {
      return (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            No modules are enabled
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Ask your administrator to enable at least one module.
          </p>
        </Card>
      );
    }

    if (currentView === 'dashboard' && isNavModuleEnabled(moduleAccess, MODULE_DEFINITIONS[0])) {
      return renderDashboard();
    }

    if (currentView === 'cms' && isNavModuleEnabled(moduleAccess, MODULE_DEFINITIONS[1])) {
      return (
        <motion.div key="cms" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <CrmLayout />
        </motion.div>
      );
    }

    if (currentView === 'reporting' && isNavModuleEnabled(moduleAccess, MODULE_DEFINITIONS[2])) {
      return (
        <motion.div key="reporting" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <ReportingLayout />
        </motion.div>
      );
    }

    if (currentView === 'provisioning' && context.customer?.id && isNavModuleEnabled(moduleAccess, MODULE_DEFINITIONS[3])) {
      return (
        <motion.div key="provisioning" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <ProvisioningLayout customerId={context.customer.id} />
        </motion.div>
      );
    }

    if (currentView === 'chat' && context.customer?.id && isNavModuleEnabled(moduleAccess, MODULE_DEFINITIONS[4])) {
      return (
        <motion.div key="chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <ChatLayout customerId={context.customer.id} customerName={context.customer.name} />
        </motion.div>
      );
    }

    return null;
  };

  const menu = moduleNavItems(enabledModules, currentView, setCurrentView).map((item) =>
    item.id === 'dashboard' && item.active
      ? { ...item, children: <DashboardSectionLinks sections={dashboardSections} /> }
      : item
  );

  return (
    <CrmProvider>
      <AppShell
        menu={menu}
        general={general}
        user={context.user}
        topLeading={topLeading}
        context={context.customer?.name}
      >
        {renderCurrentView()}
      </AppShell>
      <CrmOverlays />
    </CrmProvider>
  );
}

type StaffShellView = 'admin' | 'workspace';

function StaffExperience({ context }: { context: SessionContext }) {
  const isAdmin = context.user.role === 'admin';
  const setCurrentView = useStore((state) => state.setCurrentView);
  const [shellView, setShellView] = useState<StaffShellView>(
    isAdmin && !context.customer ? 'admin' : 'workspace'
  );

  // When a client becomes active while on admin, stay on admin until user chooses workspace.
  // Auto-open workspace only when we already intend to (default for technicians).
  useEffect(() => {
    if (!isAdmin && context.customer && shellView === 'admin') {
      setShellView('workspace');
    }
  }, [context.customer, isAdmin, shellView]);

  const switcher = (
    <ClientSwitcher
      activeCustomer={context.customer}
      isAdmin={isAdmin}
      onOpenAdmin={isAdmin ? () => setShellView('admin') : undefined}
    />
  );

  const general: ShellNavItem[] = isAdmin
    ? [
        {
          id: 'admin',
          label: 'Admin Console',
          icon: Settings2,
          active: shellView === 'admin',
          onSelect: () => setShellView('admin'),
        },
      ]
    : [];

  if ((shellView === 'admin' && isAdmin) || !context.customer) {
    // Staff see every module once a client is chosen; picking one leaves the console.
    const menu = context.customer
      ? moduleNavItems(MODULE_DEFINITIONS, null, (view) => {
          setCurrentView(view);
          setShellView('workspace');
        })
      : [];

    return (
      <AppShell menu={menu} general={general} user={context.user} topLeading={switcher}>
        {shellView === 'admin' && isAdmin ? (
          <AdminConsole />
        ) : (
          <Card className="mx-auto max-w-lg">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Select a client
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Use the client switcher at the top to pick which customer PBX to operate —
              no need to log in as that client. Selection applies to dashboard, CMS, provisioning,
              and chat.
            </p>
          </Card>
        )}
      </AppShell>
    );
  }

  return (
    <CustomerExperience
      key={context.customer.id}
      context={context}
      topLeading={switcher}
      general={general}
    />
  );
}

function AuthenticatedApp() {
  const sessionContext = useQuery(backendApi.tenancy.getSessionContext, {}) as
    | SessionContext
    | null
    | undefined;

  if (sessionContext === undefined) {
    return <LoadingScreen text="Loading account..." />;
  }

  if (sessionContext === null) {
    return <UnprovisionedAccount />;
  }

  if (sessionContext.user.role === 'admin' || sessionContext.user.role === 'technician') {
    return <StaffExperience context={sessionContext} />;
  }

  return <CustomerExperience context={sessionContext} />;
}

function App() {
  if (isDesktopPopupWindow()) return <DesktopPopupApp />;
  return <WebApp />;
}

function WebApp() {
  const session = authClient.useSession();

  if (session.isPending) {
    return <LoadingScreen text="Checking session..." />;
  }

  return (
    <>
      {session.data?.user ? <AuthenticatedApp /> : <AppLoginForm />}
      <Toaster
        position="bottom-right"
        reverseOrder={false}
        gutter={8}
        containerStyle={{
          bottom: 16,
          right: 16,
          top: 'auto',
          maxWidth: 'calc(100vw - 32px)',
          pointerEvents: 'none',
        }}
        toastOptions={{
          className: 'dark:bg-gray-800 dark:text-white',
          duration: 4000,
          style: {
            maxWidth: 'min(520px, calc(100vw - 32px))',
            overflowWrap: 'anywhere',
          },
        }}
      />
    </>
  );
}

export default App;
